import type { ApiData, Metadata } from "./types";
import type { RouteParams } from "nextjs/types";

import type { Route } from "nextjs-routes";

import config from "configs/app";
import { DEFAULT_OG_IMAGE, SITE_NAME } from "lib/seoConfig";
import getNetworkTitle from "lib/networks/getNetworkTitle";
import { currencyUnits } from "lib/units";

import compileValue from "./compileValue";
import getCanonicalUrl from "./getCanonicalUrl";
import getPageOgType from "./getPageOgType";
import * as templates from "./templates";

export default function generate<Pathname extends Route["pathname"]>(
  route: RouteParams<Pathname>,
  apiData: ApiData<Pathname> = null,
): Metadata {
  const params = {
    ...route.query,
    ...apiData,
    network_name: config.chain.name,
    network_title: getNetworkTitle(),
    network_gwei: currencyUnits.gwei,
  };

  const title = compileValue(
    templates.title.make(route.pathname, Boolean(apiData)),
    params,
  );
  const description = compileValue(
    templates.description.make(route.pathname, Boolean(apiData)),
    params,
  );

  const pageOgType = getPageOgType(route.pathname);

  // 使用新的 SEO 配置
  const ogDescription =
    pageOgType !== "Regular page"
      ? config.meta.og.description || config.meta.description
      : "";
  const ogImageUrl =
    pageOgType !== "Regular page"
      ? config.meta.og.imageUrl || DEFAULT_OG_IMAGE
      : DEFAULT_OG_IMAGE;

  // 页面级 SEO keywords（references 相关页面使用独立关键词）
  const referenceKeywords =
    "reference key, Morph reference, reference ID, transaction by reference, blockchain reference explorer, reference key explorer";
  const keywords =
    route.pathname === "/references" ||
    route.pathname === "/reference/[reference]"
      ? referenceKeywords
      : undefined;

  return {
    title: title,
    description,
    ...(keywords != null && { keywords }),
    opengraph: {
      title: title,
      description: ogDescription,
      imageUrl: ogImageUrl,
      siteName: SITE_NAME,
    },
    canonical: getCanonicalUrl(route.pathname),
  };
}
